import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Edit, Loader2, Hash, Tag, Truck, Package, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const UNITS = [
  { value: 'piece', label: 'Piece' },
  { value: 'box', label: 'Box' },
  { value: 'kg', label: 'Kilogram' },
  { value: 'g', label: 'Gram' },
  { value: 'l', label: 'Liter' },
  { value: 'ml', label: 'Milliliter' },
  { value: 'pack', label: 'Pack' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'tube', label: 'Tube' },
  { value: 'strip', label: 'Strip' },
];

const emptyForm = {
  sku: '', barcode: '', name: '', name_ar: '',
  category_id: '', supplier_id: '',
  cost: '', price: '', pharmacist_margin: '',
  currency: 'USD', unit: 'piece',
  on_hand_qty: '', min_qty: '',
  moh_code: '', country_of_origin: '',
  description: '',
  is_taxable: true, is_active: true,
  is_pos_item: false, show_image_in_pos: true,
};

/**
 * QuickAddInventoryDialog
 * Full inventory item add/edit dialog matching Inventory module.
 * Props:
 *   open, onOpenChange, mode ('add'|'edit'),
 *   editData (item object for edit), organizationId, onSaved(item)
 */
const QuickAddInventoryDialog = ({
  open, onOpenChange, mode = 'add',
  editData = null, organizationId, onSaved,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (!open || !organizationId) return;
    fetchLookups();
    if (mode === 'edit' && editData) {
      setForm({
        sku: editData.sku || editData.item_code || '',
        barcode: editData.barcode || '',
        name: editData.name || '',
        name_ar: editData.name_ar || '',
        category_id: editData.category_id || '',
        supplier_id: editData.supplier_id || '',
        cost: editData.cost ?? '',
        price: editData.price ?? '',
        pharmacist_margin: editData.pharmacist_margin ?? '',
        currency: editData.currency || 'USD',
        unit: editData.unit || 'piece',
        on_hand_qty: editData.on_hand_qty ?? '',
        min_qty: editData.min_qty ?? '',
        moh_code: editData.moh_code || '',
        country_of_origin: editData.country_of_origin || '',
        description: editData.description || '',
        is_taxable: editData.is_taxable ?? true,
        is_active: editData.is_active ?? true,
        is_pos_item: editData.is_pos_item ?? false,
        show_image_in_pos: editData.show_image_in_pos ?? true,
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, mode, editData, organizationId]);

  const fetchLookups = async () => {
    try {
      const [catRes, supRes] = await Promise.all([
        axios.get(`${API}/inventory-categories?organization_id=${organizationId}`),
        axios.get(`${API}/inventory-suppliers?organization_id=${organizationId}`),
      ]);
      setCategories(catRes.data || []);
      setSuppliers(supRes.data || []);
    } catch { /* non-blocking */ }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        sku: form.sku || null,
        barcode: form.barcode || null,
        name: form.name.trim(),
        name_ar: form.name_ar || null,
        category_id: form.category_id || null,
        supplier_id: form.supplier_id || null,
        cost: parseFloat(form.cost) || 0,
        price: parseFloat(form.price) || 0,
        pharmacist_margin: form.pharmacist_margin !== '' ? parseFloat(form.pharmacist_margin) : null,
        currency: form.currency,
        unit: form.unit,
        on_hand_qty: parseFloat(form.on_hand_qty) || 0,
        min_qty: parseFloat(form.min_qty) || 0,
        moh_code: form.moh_code || null,
        country_of_origin: form.country_of_origin || null,
        description: form.description || null,
        is_taxable: form.is_taxable,
        is_active: form.is_active,
        is_pos_item: form.is_pos_item,
        show_image_in_pos: form.show_image_in_pos,
        organization_id: organizationId,
      };

      let res;
      if (mode === 'edit' && editData?.id) {
        res = await axios.put(`${API}/inventory/${editData.id}`, payload);
      } else {
        res = await axios.post(`${API}/inventory`, payload);
      }
      if (onSaved) onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ') : (detail || err.message || 'Failed to save item');
      alert(msg);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'add' ? <Plus className="w-5 h-5 text-emerald-500" /> : <Edit className="w-5 h-5 text-emerald-500" />}
            {mode === 'add' ? 'Add Inventory Item' : 'Edit Inventory Item'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Row 1: Code, Name EN, Name AR */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Hash className="w-3 h-3" />Item Code</Label>
              <Input placeholder="e.g., 1, 2, 3..." value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="font-mono" data-testid="qa-item-code" />
            </div>
            <div className="space-y-2">
              <Label>Name (English) *</Label>
              <Input placeholder="Item name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required data-testid="qa-item-name" />
            </div>
            <div className="space-y-2">
              <Label>Name (Arabic)</Label>
              <Input placeholder="اسم المنتج" value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })} dir="rtl" />
            </div>
          </div>

          {/* Barcode */}
          <div className="space-y-2">
            <Label>Barcode</Label>
            <Input placeholder="Enter barcode..." value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              data-testid="qa-item-barcode" />
          </div>

          {/* Category + Supplier */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Tag className="w-3 h-3" />Category</Label>
              <Select value={form.category_id || 'none'} onValueChange={(v) => setForm({ ...form, category_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.cat_id || cat.id} value={cat.cat_id || cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Truck className="w-3 h-3" />Supplier</Label>
              <Select value={form.supplier_id || 'none'} onValueChange={(v) => setForm({ ...form, supplier_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Supplier</SelectItem>
                  {suppliers.map(sup => (
                    <SelectItem key={sup.code || sup.id} value={sup.code || sup.id}>{sup.code} - {sup.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cost, Price, Margin, Currency, Unit */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="space-y-2">
              <Label>Cost</Label>
              <Input type="number" step="0.01" placeholder="0.00" value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                data-testid="qa-item-cost" />
            </div>
            <div className="space-y-2">
              <Label>Price</Label>
              <Input type="number" step="0.01" placeholder="0.00" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="qa-item-price" />
            </div>
            <div className="space-y-2">
              <Label>Margin %</Label>
              <Input type="number" step="0.01" placeholder="0.00" value={form.pharmacist_margin}
                onChange={(e) => setForm({ ...form, pharmacist_margin: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="LBP">LBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quantities */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>On Hand Qty</Label>
              <Input type="number" step="0.01" placeholder="0" value={form.on_hand_qty}
                onChange={(e) => setForm({ ...form, on_hand_qty: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Min Qty (Alert)</Label>
              <Input type="number" step="0.01" placeholder="0" value={form.min_qty}
                onChange={(e) => setForm({ ...form, min_qty: e.target.value })} />
            </div>
          </div>

          {/* MOH # and Origin */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>MOH #</Label>
              <Input placeholder="Ministry of Health code" value={form.moh_code}
                onChange={(e) => setForm({ ...form, moh_code: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Origin</Label>
              <Input placeholder="Country of origin" value={form.country_of_origin}
                onChange={(e) => setForm({ ...form, country_of_origin: e.target.value })} />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea placeholder="Additional details..." value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>

          {/* Checkboxes */}
          <div className="flex items-center space-x-2 py-1">
            <Checkbox id="qa-is-taxable" checked={form.is_taxable}
              onCheckedChange={(c) => setForm({ ...form, is_taxable: c })} />
            <Label htmlFor="qa-is-taxable" className="flex items-center gap-2 cursor-pointer">
              <Receipt className="w-4 h-4 text-muted-foreground" />Subject to Tax
            </Label>
          </div>

          {/* POS Settings */}
          <div className="border-t pt-3">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Package className="w-4 h-4 text-cyan-400" />POS Quick Item Settings
            </h4>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="qa-is-pos" checked={form.is_pos_item}
                  onCheckedChange={(c) => setForm({ ...form, is_pos_item: c })} />
                <Label htmlFor="qa-is-pos" className="cursor-pointer text-sm">Show in POS Quick Items</Label>
              </div>
              {form.is_pos_item && (
                <div className="flex items-center space-x-2">
                  <Checkbox id="qa-pos-img" checked={form.show_image_in_pos}
                    onCheckedChange={(c) => setForm({ ...form, show_image_in_pos: c })} />
                  <Label htmlFor="qa-pos-img" className="cursor-pointer text-sm">Display Image in POS</Label>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="qa-item-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="qa-item-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Saving...</> : mode === 'add' ? 'Add Item' : 'Update Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddInventoryDialog;
